<template>
  <div class="tu-prq-modal" @mousedown.self="$emit('close')">
    <div class="tu-prq-card" role="dialog" aria-modal="true" :aria-labelledby="titleId">
      <button type="button" class="tu-prq-x" aria-label="Close" @click="$emit('close')">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6 6 18" /></svg>
      </button>
      <div class="tu-prq-head">
        <span class="tu-prq-ico" aria-hidden="true"><tu-ui-icon :name="icon" :size="22" /></span>
        <div>
          <span class="tu-prq-kicker">{{ kicker }}</span>
          <h2 :id="titleId" class="tu-prq-title">{{ title }}</h2>
        </div>
      </div>

      <!-- Overview -->
      <p v-if="about" class="tu-prq-about">{{ about }}</p>

      <!-- How many programs ask for it (path view only) -->
      <p v-if="item" class="tu-prq-reach">
        <b>{{ askPct }}%</b> of {{ programsNoun }} ask for it<template v-if="item.required">, <b>{{ item.required.toLocaleString('en-US') }}</b> require it</template>
      </p>

      <!-- What's required -->
      <div v-if="row || needs.length" class="tu-prq-needs">
        <h3 class="tu-prq-sub">What’s required</h3>
        <ul>
          <li v-if="row" :class="{ 'is-key': row.level === 'required' }">{{ row.level_label }}</li>
          <li v-for="n in needs" :key="n">{{ n }}</li>
        </ul>
      </div>

      <!-- Where to finish it: partner courses -->
      <div v-if="options.length" class="tu-prq-finish">
        <h3 class="tu-prq-sub">Finish this prereq</h3>
        <a
          v-for="(o, i) in options"
          :key="o.id"
          :href="o.href"
          target="_blank"
          rel="noopener noreferrer"
          class="tu-prq-opt"
          :class="{ 'is-main': i === 0 }"
          @click="$emit('finish', o.id)"
        >
          <span class="tu-prq-opt__logo"><img :src="o.logo" :alt="o.name" /></span>
          <span class="tu-prq-opt__text"><b>{{ o.name }}</b><small>{{ o.sub }}</small></span>
          <span class="tu-prq-opt__btn">{{ o.cta }}</span>
        </a>
      </div>

      <router-link v-if="item && actionLabel && actionHref" :to="actionHref" target="_blank" rel="noopener" class="tu-prq-action" @click.native="$emit('action')">{{ actionLabel }}<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m9 5 7 7-7 7"/></svg></router-link>
      <button v-else-if="item && actionLabel" type="button" class="tu-prq-action" @click="$emit('action')">{{ actionLabel }}</button>
    </div>
  </div>
</template>

<script>
import TuUiIcon from '@/tu/components/UiIcon.vue'
import { prereqOptions } from '@/tu/utils/prereqRoutes'

export const PREREQ_GROUP_ICONS = {
  college_course: 'book_open', entrance_exam: 'edit_note', certification: 'medal',
  compliance: 'verify', general: 'profile', experience: 'users_three', prep_course: 'book_open'
}

/**
 * One prerequisite, opened from a list. Pass `item` (a summary across many programs, from
 * public-summary) or `row` (one school's requirement, from public). Shows an overview, what is
 * required, and the partner courses a student can finish it with. Emits close, finish and action.
 */
export default {
  name: 'TuPrereqModal',
  components: { TuUiIcon },
  props: {
    item: { type: Object, default: null },
    row: { type: Object, default: null },
    group: { type: String, default: '' },
    programLabel: { type: String, default: '' },
    total: { type: Number, default: 0 },
    actionLabel: { type: String, default: '' },
    actionHref: { type: String, default: '' }
  },
  computed: {
    titleId() { return `tu-prq-${(this.item && this.item.key) || (this.row && this.row.id) || 'x'}` },
    type() { return this.group || (this.row && this.row.type) || '' },
    icon() { return PREREQ_GROUP_ICONS[this.type] || 'verify' },
    title() { return this.item ? this.item.label : (this.row && this.row.name) || '' },
    kicker() {
      if (this.row) return this.row.type_label || 'Prerequisite'
      return this.programLabel ? `${this.programLabel} prerequisite` : 'Prerequisite'
    },
    about() { return this.item ? this.item.about : (this.row && this.row.item_about) || '' },
    itemKey() { return this.item ? this.item.key : (this.row && this.row.item_key) || '' },
    options() { return prereqOptions(this.itemKey) },
    programsNoun() { return this.programLabel ? `${this.programLabel} programs` : 'programs' },
    askPct() { return Math.max(1, Math.round(((this.item && this.item.share) || 0) * 100)) },
    /** Short requirement chips: grade, score, lab, recency, due. */
    needs() {
      const out = []
      const i = this.item
      const r = this.row
      if (i) {
        if (this.type === 'college_course') {
          if (i.common_grade) out.push(`${i.common_grade} or better`)
          if (i.lab_share != null && i.lab_share >= 0.5) out.push('Lab usually required')
          if (i.median_recency_years) out.push(`Within ${i.median_recency_years} years`)
        }
        if (this.type === 'entrance_exam' && i.median_score != null) out.push(`Typical score ${i.median_score}`)
        if (i.common_due_by) out.push(`Due ${i.common_due_by.charAt(0).toLowerCase()}${i.common_due_by.slice(1)}`)
      } else if (r) {
        if (r.min_grade) out.push(`${r.min_grade} or better`)
        if (r.min_score != null) out.push(`Score ${r.min_score}${r.score_label ? ` ${r.score_label}` : ''}`)
        if (r.credits) out.push(`${r.credits} credits${r.has_lab ? ' with lab' : ''}`)
        else if (r.has_lab) out.push('Lab required')
        if (r.recency_years) out.push(`Within ${r.recency_years} years`)
        if (r.due_by_label) out.push(`Due ${r.due_by_label.charAt(0).toLowerCase()}${r.due_by_label.slice(1)}`)
        if (r.choice_group) out.push(`One of: ${r.choice_group}`)
      }
      return out
    }
  }
}
</script>

<style scoped>
.tu-prq-modal { position: fixed; inset: 0; z-index: 2000; display: grid; place-items: center; padding: 16px; background: rgba(15, 18, 24, .5); }
.tu-prq-card { position: relative; width: min(520px, 100%); max-height: calc(100vh - 32px); overflow-y: auto; padding: 28px 28px 24px; border-radius: 18px; background: var(--tu-surface, #fff); box-shadow: 0 24px 60px rgba(15, 18, 24, .25); box-sizing: border-box; }
.tu-prq-x { position: absolute; top: 14px; right: 14px; display: grid; place-items: center; width: 36px; height: 36px; border: 0; border-radius: 50%; background: var(--tu-surface-2, #f3f5f8); color: var(--tu-ink); cursor: pointer; }
.tu-prq-x svg { width: 18px; height: 18px; }
.tu-prq-head { display: flex; align-items: center; gap: 14px; padding-right: 40px; }
.tu-prq-ico { display: grid; place-items: center; flex: none; width: 48px; height: 48px; border-radius: 14px; background: var(--tu-blue-tint); color: var(--tu-blue-deep); }
.tu-prq-ico .tu-ui-icon { display: inline-flex; }
.tu-prq-kicker { display: block; font-size: 12px; font-weight: 700; letter-spacing: .1em; text-transform: uppercase; color: var(--tu-blue); }
.tu-prq-title { margin: 4px 0 0; font-family: var(--tu-font-display); font-size: 24px; line-height: 1.2; letter-spacing: -.015em; color: var(--tu-ink); }
.tu-prq-about { margin: 14px 0 0; font-size: 15px; line-height: 1.6; color: var(--tu-ink-soft); }
.tu-prq-reach { margin: 12px 0 0; font-size: 14.5px; color: var(--tu-ink-soft); }
.tu-prq-reach b { font-weight: 700; color: var(--tu-ink); }

.tu-prq-sub { margin: 0 0 10px; font-family: var(--tu-font-text); font-size: 13px; font-weight: 700; letter-spacing: .06em; text-transform: uppercase; color: var(--tu-ink-mute); }
.tu-prq-needs { margin-top: 20px; }
.tu-prq-needs ul { list-style: none; display: flex; flex-wrap: wrap; gap: 8px; margin: 0; padding: 0; }
.tu-prq-needs li { padding: 6px 12px; border-radius: 999px; background: var(--tu-surface-2, #f3f5f8); font-size: 13.5px; font-weight: 600; color: var(--tu-ink); }
.tu-prq-needs li.is-key { background: var(--tu-blue-tint); color: var(--tu-blue-deep); }

.tu-prq-finish { margin-top: 22px; }
.tu-prq-opt { display: flex; align-items: center; gap: 14px; padding: 14px; border: 1px solid var(--tu-line); border-radius: 14px; background: #fff; text-decoration: none !important; color: inherit; transition: border-color .15s ease, box-shadow .15s ease; }
.tu-prq-opt + .tu-prq-opt { margin-top: 10px; }
.tu-prq-opt:hover { border-color: var(--tu-blue); box-shadow: 0 2px 10px rgba(24, 24, 24, .06); }
.tu-prq-opt.is-main { border-color: var(--tu-blue); }
.tu-prq-opt__logo { display: grid; place-items: center; flex: none; width: 76px; height: 44px; padding: 6px; border-radius: 10px; border: 1px solid var(--tu-line); background: #fff; box-sizing: border-box; }
.tu-prq-opt__logo img { max-width: 100%; max-height: 28px; object-fit: contain; }
.tu-prq-opt__text { display: flex; flex-direction: column; gap: 2px; flex: 1; min-width: 0; }
.tu-prq-opt__text b { font-size: 15px; font-weight: 700; color: var(--tu-ink); }
.tu-prq-opt__text small { font-size: 13px; line-height: 1.4; color: var(--tu-ink-soft); }
.tu-prq-opt__btn { flex: none; padding: 9px 14px; border-radius: 10px; background: var(--tu-surface-2, #f3f5f8); font-size: 14px; font-weight: 700; color: var(--tu-blue-deep, var(--tu-blue)); white-space: nowrap; }
.tu-prq-opt.is-main .tu-prq-opt__btn { background: var(--tu-blue); color: #fff; }

.tu-prq-action { display: flex; align-items: center; justify-content: center; width: 100%; min-height: 46px; margin-top: 18px; border: 0; border-radius: var(--tu-r-sm, 10px); background: var(--tu-cta); color: var(--tu-on-cta); font: 700 15px var(--tu-font-text); cursor: pointer; }
.tu-prq-action:hover { background: var(--tu-cta-deep); }
.tu-prq-action svg { width: 18px; height: 18px; margin-left: 6px; }
a.tu-prq-action { text-decoration: none !important; color: var(--tu-on-cta) !important; }
@media (max-width: 540px) {
  .tu-prq-card { padding: 22px 18px 18px; }
  .tu-prq-title { font-size: 21px; }
  .tu-prq-opt { flex-wrap: wrap; }
  .tu-prq-opt__btn { width: 100%; box-sizing: border-box; text-align: center; }
}
</style>
